import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import {
  AmbientLight,
  DirectionalLight,
  MeshStandardMaterial,
  MeshPhongMaterial,
  LineBasicMaterial,
  TextureLoader,
  Box3,
  Vector3,
  WireframeGeometry,
  LineSegments,
  DoubleSide,
  Box3Helper,
  Color
} from 'three';

const ModelViewer = forwardRef(
  ({ scale, setPointA, setPointB, objFile, textureFile, pointA, pointB }, ref) => {
    const { scene, camera, gl } = useThree();
    const modelRef = useRef(null);
    const controls = useRef();
    const [isModelReady, setIsModelReady] = useState(false);
    const [markerScale, setMarkerScale] = useState(0.05);
    const [markerColors, setMarkerColors] = useState({ A: 'gray', B: 'gray' });
    const [resetPoints, setResetPoints] = useState({ A: null, B: null });
    const [showMesh, setShowMesh] = useState(false);
    const originalMaterials = useRef(new Map());
    const wireframeLines = useRef([]);
    const cropBoxHelperRef = useRef(null);
    const lastMarkerScaleRef = useRef(0.05);

    // Cream/off-white material for mesh mode
    const meshModeMaterial = useRef(new MeshPhongMaterial({
      color: 0xfff8e7,
      side: DoubleSide
    }));

    // Black wireframe material
    const wireframeMaterial = useRef(new LineBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.25
    }));

    useImperativeHandle(ref, () => ({
      resetCamera() {
        controls.current.reset();
        adjustCameraToFitModel();
      },
      resetMarker(point) {
        if (point === 'A') {
          setMarkerColors((prev) => ({ ...prev, A: 'gray' }));
          setResetPoints((prev) => ({ ...prev, A: pointA }));
          setPointA(null);
        } else if (point === 'B') {
          setMarkerColors((prev) => ({ ...prev, B: 'gray' }));
          setResetPoints((prev) => ({ ...prev, B: pointB }));
          setPointB(null);
        }
      },
      getModelScene() {
        return modelRef.current;
      },
      toggleMeshMode() {
        setShowMesh(prev => !prev);
      },
      isMeshMode() {
        return showMesh;
      },
      // Get model bounding box for crop sliders
      getModelBounds() {
        if (!modelRef.current) return null;
        const box = new Box3().setFromObject(modelRef.current);
        const min = box.min;
        const max = box.max;
        return {
          minX: min.x * scale,
          maxX: max.x * scale,
          minY: min.y * scale,
          maxY: max.y * scale,
          minZ: min.z * scale,
          maxZ: max.z * scale
        };
      },
      // Show crop box preview
      setCropPreview(bounds) {
        // Remove existing helper
        if (cropBoxHelperRef.current) {
          scene.remove(cropBoxHelperRef.current);
        }
        if (!bounds) return;

        // Create box from bounds (convert back from scaled coordinates)
        const box = new Box3(
          new Vector3(bounds.minX / scale, bounds.minY / scale, bounds.minZ / scale),
          new Vector3(bounds.maxX / scale, bounds.maxY / scale, bounds.maxZ / scale)
        );

        // Create helper with orange color
        const helper = new Box3Helper(box, new Color(0xff6600));
        cropBoxHelperRef.current = helper;
        scene.add(helper);
      },
      // Clear crop preview
      clearCropPreview() {
        if (cropBoxHelperRef.current) {
          scene.remove(cropBoxHelperRef.current);
          cropBoxHelperRef.current = null;
        }
      },
      // Apply crop - filter vertices/faces by bounds
      applyCrop(bounds, objContent, currentScale) {
        if (!objContent || !bounds) return null;

        const vertices = [];
        const texCoords = [];
        const faces = [];
        const vertexMap = new Map(); // oldIndex -> newIndex

        // Parse OBJ content
        const lines = objContent.split('\n');
        let vertexIndex = 0;

        lines.forEach(line => {
          if (line.startsWith('v ')) {
            const parts = line.trim().split(/\s+/);
            const x = parseFloat(parts[1]) * currentScale;
            const y = parseFloat(parts[2]) * currentScale;
            const z = parseFloat(parts[3]) * currentScale;

            // Check if vertex is inside bounds
            if (x >= bounds.minX && x <= bounds.maxX &&
                y >= bounds.minY && y <= bounds.maxY &&
                z >= bounds.minZ && z <= bounds.maxZ) {
              vertexMap.set(vertexIndex, vertices.length);
              vertices.push({ x: x / currentScale, y: y / currentScale, z: z / currentScale });
            }
            vertexIndex++;
          } else if (line.startsWith('vt ')) {
            texCoords.push(line);
          } else if (line.startsWith('f ')) {
            faces.push(line);
          }
        });

        // Filter faces - keep only faces where all vertices are inside
        const croppedFaces = [];
        faces.forEach(line => {
          const parts = line.trim().split(/\s+/).slice(1);
          const vertexIndices = parts.map(p => parseInt(p.split('/')[0], 10) - 1);

          // Check if all vertices of this face are in the cropped set
          const allInside = vertexIndices.every(vi => vertexMap.has(vi));
          if (allInside) {
            // Remap vertex indices
            const newParts = parts.map(p => {
              const segments = p.split('/');
              const oldVi = parseInt(segments[0], 10) - 1;
              const newVi = vertexMap.get(oldVi) + 1;
              segments[0] = newVi.toString();
              return segments.join('/');
            });
            croppedFaces.push('f ' + newParts.join(' '));
          }
        });

        // Generate new OBJ content
        let newObj = '# Cropped model\nmtllib scaled_model.mtl\n\n';

        // Vertices
        vertices.forEach(v => {
          newObj += `v ${v.x} ${v.y} ${v.z}\n`;
        });

        newObj += '\n';

        // Texture coordinates (keep all, they are indexed separately)
        texCoords.forEach(t => {
          newObj += t + '\n';
        });

        newObj += '\n';

        // Faces
        croppedFaces.forEach(f => {
          newObj += f + '\n';
        });

        return newObj;
      },
      // Apply Laplacian smoothing — returns OBJ text AND updates scene geometry in-place
      applySmooth(iterations, objContent, currentScale) {
        if (!objContent) return null;

        let vertices = [];
        const texCoords = [];
        const faces = [];
        const adjacencyList = new Map(); // vertex index -> array of neighbor indices

        // Parse OBJ content
        const lines = objContent.split('\n');

        lines.forEach(line => {
          if (line.startsWith('v ')) {
            const parts = line.trim().split(/\s+/);
            vertices.push({
              x: parseFloat(parts[1]),
              y: parseFloat(parts[2]),
              z: parseFloat(parts[3])
            });
          } else if (line.startsWith('vt ')) {
            texCoords.push(line);
          } else if (line.startsWith('f ')) {
            const parts = line.trim().split(/\s+/).slice(1);
            const vertexIndices = parts.map(p => parseInt(p.split('/')[0], 10) - 1);

            faces.push({ line, indices: vertexIndices });

            // Build adjacency list
            for (let i = 0; i < vertexIndices.length; i++) {
              const v1 = vertexIndices[i];
              const v2 = vertexIndices[(i + 1) % vertexIndices.length];

              if (!adjacencyList.has(v1)) adjacencyList.set(v1, new Set());
              if (!adjacencyList.has(v2)) adjacencyList.set(v2, new Set());

              adjacencyList.get(v1).add(v2);
              adjacencyList.get(v2).add(v1);
            }
          }
        });

        // Apply Laplacian smoothing iterations
        const smoothingFactor = 0.5; // Conservative for medical use

        for (let iter = 0; iter < iterations; iter++) {
          const newVertices = vertices.map((v, idx) => {
            const neighbors = adjacencyList.get(idx);
            if (!neighbors || neighbors.size === 0) return v;

            // Calculate centroid of neighbors
            let sumX = 0, sumY = 0, sumZ = 0;
            neighbors.forEach(ni => {
              sumX += vertices[ni].x;
              sumY += vertices[ni].y;
              sumZ += vertices[ni].z;
            });

            const avgX = sumX / neighbors.size;
            const avgY = sumY / neighbors.size;
            const avgZ = sumZ / neighbors.size;

            // Blend between original and average
            return {
              x: v.x + (avgX - v.x) * smoothingFactor,
              y: v.y + (avgY - v.y) * smoothingFactor,
              z: v.z + (avgZ - v.z) * smoothingFactor
            };
          });

          vertices = newVertices;
        }

        // Update Three.js scene geometry in-place (preserves camera position + mesh mode)
        if (modelRef.current) {
          // Build triangulated vertex list matching OBJLoader's face-order output
          const triVerts = [];
          for (const face of faces) {
            const idx = face.indices;
            // Fan triangulation (same as OBJLoader)
            for (let i = 1; i < idx.length - 1; i++) {
              triVerts.push(vertices[idx[0]]);
              triVerts.push(vertices[idx[i]]);
              triVerts.push(vertices[idx[i + 1]]);
            }
          }

          // Apply smoothed positions to all mesh geometries
          const meshes = [];
          modelRef.current.traverse((child) => {
            if (child.isMesh) meshes.push(child);
          });

          let globalIdx = 0;
          for (const mesh of meshes) {
            const posAttr = mesh.geometry.attributes.position;
            const count = Math.min(posAttr.count, triVerts.length - globalIdx);
            for (let i = 0; i < count; i++) {
              const v = triVerts[globalIdx++];
              posAttr.setXYZ(i, v.x, v.y, v.z);
            }
            posAttr.needsUpdate = true;
            mesh.geometry.computeVertexNormals();
            mesh.geometry.computeBoundingSphere();
            mesh.geometry.computeBoundingBox();
          }

          // Rebuild wireframe geometries for mesh mode display
          wireframeLines.current.forEach(wfLine => {
            const parentMesh = wfLine.parent;
            if (parentMesh && parentMesh.isMesh) {
              const newWireGeom = new WireframeGeometry(parentMesh.geometry);
              wfLine.geometry.dispose();
              wfLine.geometry = newWireGeom;
            }
          });
        }

        // Generate new OBJ content
        let newObj = '# Smoothed model\nmtllib scaled_model.mtl\n\n';

        // Vertices
        vertices.forEach(v => {
          newObj += `v ${v.x} ${v.y} ${v.z}\n`;
        });

        newObj += '\n';

        // Texture coordinates
        texCoords.forEach(t => {
          newObj += t + '\n';
        });

        newObj += '\n';

        // Faces (unchanged)
        faces.forEach(f => {
          newObj += f.line + '\n';
        });

        return newObj;
      },
    }));

    const adjustCameraToFitModel = useCallback(() => {
      if (modelRef.current) {
        const boundingBox = new Box3().setFromObject(modelRef.current);
        const size = boundingBox.getSize(new Vector3());
        const center = boundingBox.getCenter(new Vector3());

        modelRef.current.position.set(
          modelRef.current.position.x - center.x,
          modelRef.current.position.y - center.y,
          modelRef.current.position.z - center.z
        );

        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = (camera.fov * Math.PI) / 180;
        const cameraDistance = maxDim / (2 * Math.tan(fov / 2));

        camera.position.set(0, 0, cameraDistance * 1.5);
        camera.lookAt(0, 0, 0);
        controls.current.update();
      }
    }, [camera]);

    useEffect(() => {
      controls.current = new OrbitControls(camera, gl.domElement);
      controls.current.enableDamping = true;
      return () => controls.current.dispose();
    }, [camera, gl]);

    useEffect(() => {
      const ambientLight = new AmbientLight(0xffffff, 0.8);
      const directionalLight = new DirectionalLight(0xffffff, 1);
      directionalLight.position.set(5, 10, 7.5);
      scene.add(ambientLight, directionalLight);

      return () => {
        scene.remove(ambientLight, directionalLight);
      };
    }, [scene]);

    useFrame(() => {
      controls.current?.update();

      if (modelRef.current) {
        const boundingBox = new Box3().setFromObject(modelRef.current);
        const center = boundingBox.getCenter(new Vector3());
        const distance = camera.position.distanceTo(center);

        const newMarkerScale = distance / 60;
        // Only update state if scale changed significantly (avoid constant re-renders)
        if (Math.abs(newMarkerScale - lastMarkerScaleRef.current) > 0.001) {
          lastMarkerScaleRef.current = newMarkerScale;
          setMarkerScale(newMarkerScale);
        }
      }
    });

    // Toggle between texture mode and mesh mode (cream color + wireframe)
    useEffect(() => {
      if (!modelRef.current || !isModelReady) return;

      modelRef.current.traverse((child) => {
        if (child.isMesh) {
          if (showMesh) {
            // Save original material and switch to cream/mesh mode
            if (!originalMaterials.current.has(child.uuid)) {
              originalMaterials.current.set(child.uuid, child.material);
            }
            child.material = meshModeMaterial.current;
          } else {
            // Restore original textured material
            const original = originalMaterials.current.get(child.uuid);
            if (original) {
              child.material = original;
            }
          }
        }
      });

      // Toggle wireframe visibility
      wireframeLines.current.forEach(line => {
        line.visible = showMesh;
      });
    }, [showMesh, isModelReady]);

    useEffect(() => {
      const loadModel = async () => {
        try {
          const objUrl = URL.createObjectURL(objFile);
          const textureUrl = textureFile ? URL.createObjectURL(textureFile) : null;

          // Clean up previous model (wireframes are children, so they get removed automatically)
          if (modelRef.current) {
            scene.remove(modelRef.current);
          }
          wireframeLines.current = [];
          originalMaterials.current.clear();

          setPointA(null);
          setPointB(null);
          setResetPoints({ A: null, B: null });
          setMarkerColors({ A: 'gray', B: 'gray' });
          setIsModelReady(false);

          const objLoader = new OBJLoader();
          const model = await objLoader.loadAsync(objUrl);

          if (textureUrl) {
            const texture = new TextureLoader().load(textureUrl);
            model.traverse((child) => {
              if (child.isMesh) {
                const material = new MeshPhongMaterial({
                  map: texture,
                  side: DoubleSide
                });
                child.material = material;

                // Create wireframe overlay for mesh mode - add as child of mesh
                const wireframeGeom = new WireframeGeometry(child.geometry);
                const wireframeLine = new LineSegments(wireframeGeom, wireframeMaterial.current);
                wireframeLine.visible = false;
                wireframeLine.raycast = () => {}; // Disable raycast to not interfere with clicks
                child.add(wireframeLine);
                wireframeLines.current.push(wireframeLine);
              }
            });
          } else {
            model.traverse((child) => {
              if (child.isMesh) {
                child.material = new MeshPhongMaterial({
                  color: 0xcccccc,
                  side: DoubleSide
                });

                // Create wireframe overlay for mesh mode - add as child of mesh
                const wireframeGeom = new WireframeGeometry(child.geometry);
                const wireframeLine = new LineSegments(wireframeGeom, wireframeMaterial.current);
                wireframeLine.visible = false;
                wireframeLine.raycast = () => {}; // Disable raycast to not interfere with clicks
                child.add(wireframeLine);
                wireframeLines.current.push(wireframeLine);
              }
            });
          }

          modelRef.current = model;
          modelRef.current.scale.set(scale, scale, scale);
          scene.add(model);
          setIsModelReady(true);

          adjustCameraToFitModel();

          return () => {
            scene.remove(model);
            wireframeLines.current.forEach(line => scene.remove(line));
            URL.revokeObjectURL(objUrl);
            if (textureUrl) URL.revokeObjectURL(textureUrl);
          };
        } catch (error) {
          console.error('Fehler beim Laden des Modells:', error);
        }
      };

      loadModel();
    }, [objFile, textureFile, scene, setPointA, setPointB, adjustCameraToFitModel, scale]);

    const handleModelClick = (event) => {
      if (!isModelReady) return;

      event.stopPropagation();
      const point = event.point.clone();

      if (!pointA) {
        setPointA(point);
        setMarkerColors((prev) => ({ ...prev, A: 'red' }));
        setResetPoints((prev) => ({ ...prev, A: null }));
      } else if (!pointB) {
        setPointB(point);
        setMarkerColors((prev) => ({ ...prev, B: 'blue' }));
        setResetPoints((prev) => ({ ...prev, B: null }));
      }
    };

    if (!isModelReady) return null;

    return (
      <>
        <primitive object={modelRef.current} onClick={handleModelClick} />
        {(pointA || resetPoints.A) && (
          <mesh position={pointA || resetPoints.A} scale={markerScale}>
            <sphereGeometry args={[1, 16, 16]} />
            <meshStandardMaterial color={markerColors.A} />
          </mesh>
        )}
        {(pointB || resetPoints.B) && (
          <mesh position={pointB || resetPoints.B} scale={markerScale}>
            <sphereGeometry args={[1, 16, 16]} />
            <meshStandardMaterial color={markerColors.B} />
          </mesh>
        )}
      </>
    );
  }
);

export default ModelViewer;
